<script>
import StoreService from '@/services/StoreService'
import MarketplaceCategories from '@/components/MarketplaceComponents/MarketplaceCategories'
import Breadcrumbs from '@/components/MarketplaceComponents/Breadcrumbs.vue'
import PageTitle from '@/components/MarketplaceComponents/PageTitle.vue'
import Pagination from '@/components/Pagination/Pagination.vue'
import ProductLine from '@/components/MarketplaceComponents/ProductLine'
import ProductCard from '@/components/MarketplaceComponents/ProductCard'
import ComicsCard from '@/components/MarketplaceComponents/ComicsCard'
import PageArticle from '@/components/MarketplaceComponents/PageArticle'
import ItemsViewSwitcher from '@/components/MarketplaceComponents/ItemsViewSwitcher'
import { mapActions, mapGetters, mapState } from 'vuex'
import { createMeta } from '~/utils/meta'

export default {
  components: {
    ItemsViewSwitcher,
    Breadcrumbs,
    ComicsCard,
    MarketplaceCategories,
    Pagination,
    ProductCard,
    ProductLine,
    PageArticle,
    PageTitle
  },

  async asyncData ({ $axios, route }) {
    const { data } = await StoreService.getMarketplaceAppByPath($axios, route.params.app)

    const application = data?.data

    const productsData = await StoreService.getMarketplaceProductsWithUserID($axios, 1, 12, '', false, null, application?.id)

    const products = productsData?.data?.data?.products
    const productsCount = +productsData?.data?.data?.count?.[0]?.count

    const is404 = !productsCount

    return {
      application,
      products,
      productsCount,
      is404
    }
  },

  data () {
    return {
      application: null,
      products: null,
      productsCount: 0,
      page: 0,
      pageSize: 10,
      is404: false
    }
  },

  computed: {
    ...mapState('user', ['user']),
    ...mapState('marketplace', ['view']),
    ...mapGetters('user', ['isAuthorized', 'isAdmin']),

    activeTabId () {
      return this.application?.app_type || this.tabs[0].id
    },

    appPath () {
      return this.$route.params.app
    },

    currentTabTitle () {
      return this.tabs?.find(tab => tab.id === this.activeTabId)?.title
    },

    appName () {
      return this.application?.title || ''
    },

    breadcrumbs () {
      return [
        { title: this.$t('marketplace'), to: '/marketplace' },
        {
          title: this.appName
        }
      ]
    },

    goBackPath () {
      return '/'
    },

    categories () {
      return this.application?.categories
    },

    categoriesWithItems () {
      return this.categories?.filter(c => c.count > 0 || this.isAdmin)
    },

    article () {
      const lang = this.$i18n.locale.includes('en') ? 'en' : this.$i18n.locale

      return this.application?.seo_data?.[lang]?.article
    },

    h1Title () {
      const title = this.$t('appItems', { name: this.appName })

      const lang = this.$i18n.locale.includes('en') ? 'en' : this.$i18n.locale

      return this.application?.seo_data?.[lang]?.h1 || title
    },

    isSeller () {
      return this.user?.is_seller
    },

    pagesCount () {
      if (!this.productsCount || this.productsCount === 0) {
        return 1
      }

      return Math.ceil(this.productsCount / this.pageSize)
    },

    canCreateService () {
      return this.isSeller && this.application?.app_type !== 'read'
    },

    showCategories () {
      return !this.noCategoriesApp
    },

    appType () {
      return this.application?.app_type
    },

    noCategoriesApp () {
      return [
        'social',
        'app',
        'read'
      ].includes(this.appType)
    }
  },

  watch: {
    async appPath () {
      await this.loadApp()
      this.page = 0
      this.loadProducts()
    },

    pageSize () {
      this.page = 0
      this.loadProducts()
    },

    page () {
      this.loadProducts()
    }
  },

  methods: {
    ...mapActions('modal', ['showModal']),

    async loadApp () {
      const { data } = await StoreService.getMarketplaceAppByPath(this.$axios, this.appPath)

      this.application = data?.data
    },

    async loadProducts () {
      const { data } = await StoreService.getMarketplaceProductsWithUserID(this.$axios, this.page + 1, this.pageSize, '', false, this.application?.categories?.map(c => c.id))

      this.products = data?.data?.products
      this.productsCount = +data?.data?.count?.[0]?.count

      this.is404 = !this.productsCount
    },

    categoryName (category) {
      return category.translations?.[this.$i18n.locale.split('-')[0]]?.category_name || this.$t(category.category_name?.trim())
    },

    productDescription (product) {
      if (product.short_description && product.short_description !== '') {
        return product.short_description
      }

      return product.description
    },

    appWithCategoryLink (category) {
      return this.localeLocation(`/marketplace/${this.appPath}/${category?.seo_data?.path}`)
    },

    openAuthModal () {
      this.showModal({
        component: 'Auth',
        data: {
          mode: 'register'
        }
      })
    },

    addNewService () {
      if (!this.isAuthorized || !this.isSeller) {
        this.$router.push(this.localeLocation('/marketplace/become-seller'))
      } else if (this.isSeller) {
        this.$router.push(this.localeLocation(`/marketplace/add?type=${this.activeTabId}&app=${this.application.id}`))
      }
    }
  },

  head () {
    if (this.$i18n.locale === 'ru') {
      const title = this.$t('applicationPageMetaTitle', {
        name: this.application?.title || '',
        categories: this.application?.categories?.map(c => this.$t(c.category_name))?.join(', ')
      })
      const description = this.$t('applicationPageMetaDescription', {
        name: this.application?.title || '',
        categories: this.application?.categories?.map(c => this.$t(c.category_name))?.join(', ')
      })

      const lang = this.$i18n.locale.includes('en') ? 'en' : this.$i18n.locale

      return createMeta.call(this, {
        title: this.application?.seo_data?.[lang]?.title || title,
        description: this.application?.seo_data?.[lang]?.description || description
      })
    }
    return createMeta.call(this, {
      title: this.$t('applicationPageMetaTitle', {
        name: this.application?.title || '',
        categories: this.application?.categories?.map(c => this.$t(c.category_name))?.join(', ')
      }),
      description: this.$t('applicationPageMetaDescription', {
        name: this.application?.title || '',
        categories: this.application?.categories?.map(c => this.$t(c.category_name))?.join(', ')
      })
    })
  }
}
</script>

<template>
  <div :class="$style.page">
    <Breadcrumbs :list="breadcrumbs" />

    <div v-if="application" :class="$style.page__body">
      <PageTitle with-go-back :go-back-path="goBackPath" :title="h1Title" />

      <div v-if="is404" :class="$style.block404">
        <img :class="$style.img404" :src="require('~/assets/img/404.webp')" :alt="$t('errorCode2')" />

        <p :class="$style.text404">
          {{ $t("marketplaceText404") }}
        </p>

        <div :class="$style.buttons404">
          <Button is-primary-transparent @click="$router.push(localeLocation('/marketplace'))">
            {{ $t("goBack") }}
          </Button>
          <Button is-primary @click="addNewService">
            {{ $t("listAnItem") }}
          </Button>
        </div>
      </div>

      <template v-else>
        <MarketplaceCategories is-outside one-line />

        <Button is-primary :class="$style.seller" @click="addNewService">
          {{ $t(isSeller ? 'createService' : 'becomeASeller') }}
        </Button>

        <div v-if="showCategories" :class="$style.tags">
          <NuxtLink v-for="category in categoriesWithItems" :key="category.id" :class="$style.tag" :to="appWithCategoryLink(category)">
            {{ categoryName(category) }} ({{ category.count }})
          </NuxtLink>
        </div>

        <ItemsViewSwitcher v-if="application.app_type !== 'read'" />

        <template v-if="products">
          <div v-if="view === 'cards' || application.app_type === 'read'" :class="{[$style.products]: true, [$style.productsComics]: application.app_type === 'read'}">
            <div v-for="product in products" :key="product.id">
              <ComicsCard v-if="application.app_type === 'read'" :product="product" />
              <ProductCard v-else :product="product" :with-category-text="false" with-seller-text />
            </div>
          </div>

          <div v-else :class="$style.productsLines">
            <div v-for="product in products" :key="product.id">
              <ProductLine :product="product" with-seller-text />
            </div>
          </div>
        </template>

        <Pagination
          :class="$style.pagination"
          :per-page="pageSize"
          :pages-count="pagesCount"
          :current-page="page"
          :show-by-array="[5,10,20,50,100]"
          @perPageChange="(pp) => { pageSize = pp }"
          @pageChange="(p) => { page = p }"
        />
      </template>
    </div>

    <template v-if="article">
      <PageArticle :content="article" />
    </template>
  </div>
</template>

<style lang="scss" module>
.page {
  width: 100%;

  &__body {
    padding: 16px;
  }
}

.block404 {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  max-width: 416px;
  margin: auto;
}

.img404 {
  width: 100%;
  max-width: 416px;
  object-fit: contain;
}

.text404 {
  color: $white;
  font-size: 22px;
  font-weight: 700;
  line-height: 30px;
  text-align: center;

  @media (min-width: $breakpointDesktopWide) {
    font-size: 28px;
    line-height: 36px;
  }
}

.buttons404 {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
}

.button404 {
  background: $primary60-20;
  color: $primary60;
}

.seller {
  width: 100% !important;
  margin-bottom: 24px;
}

.tags {
  display: flex;
  flex-wrap: wrap;
}

.tag {
  padding: 8px 12px;
  background: $blue-grey50;
  color: $white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  margin-right: 8px;
  margin-bottom: 8px;
  border: 1px solid $blue-grey50;
  cursor: pointer;

  &:last-child {
    margin-right: 0;
  }
}

.productsLines {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 16px;
  margin-top: 24px;
}

.products {
  --amount: 1;
  display: grid;
  grid-template-columns: repeat(var(--amount), calc((100% - (8px * (var(--amount) - 1) ) ) / var(--amount)) );
  grid-gap: 8px;
  margin-top: 24px;

  @media (min-width: $breakpointMobile) {
    --amount: 2;
  }

  @media (min-width: $breakpointTablet) {
    --amount: 3;
  }

  @media (min-width: $breakpointDesktop) {
    --amount: 4;
  }

  @media (min-width: $breakpointDesktopWide) {
    --amount: 5;
  }

  &Comics {
    grid-template-columns: 1fr;
    grid-gap: 16px;

    @media (min-width: $breakpointMobileSmall) {
      grid-template-columns: repeat(2, 50%);
      max-width: 100%;
    }

    @media (min-width: $breakpointTablet) {
      grid-template-columns: repeat(3, 1fr);
    }

    @media (min-width: $breakpointDesktopWide) {
      grid-template-columns: repeat(5, 1fr);
    }
  }
}

.product {
  display: block;
  height: 100%;
  background: $blue-grey70;
  color: $white;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;

  &__description {
    width: 100%;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
    color: $white;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    padding-top: 12px;
    margin-top: 12px;
    border-top: 1px solid $blue-grey40;
  }

  &__dealer {
    display: grid;
    grid-template-columns: max-content 1fr;
    grid-gap: 8px;

    &Right {
      display: grid;
      grid-template-columns: 1fr;
      grid-gap: 4px;

      &Top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 4px;
      }

      &Bottom {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
    }

    &Name {
      color: $white;
      font-size: 16px;
      font-weight: 500;
      line-height: 20px;
    }

    &Avatar {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      object-fit: cover;
    }

    &Reviews {
      color: $blue-grey10;
      font-size: 12px;
      font-weight: 400;
      line-height: 20px;
    }

    &Price {
      color: $white;
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
    }
  }
}

.pagination {
  margin: 40px 0;
}
</style>
